import React, { useMemo, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import type { SelectProps } from 'antd/es/select';
import debounce from 'lodash/debounce';
import { Method, buildURL } from '@/fetch/core/util';
import { request } from '@/fetch/core';
import { HttpStatusCode } from '@/fetch/core/constant';
import './index.scss';
import CollectMapApi from '@/fetch/bussiness/collectMap';
import { CockpitMode } from '@/utils/type';
export interface DebounceSelectProps<ValueType = any>
  extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  fetchOptions: (search: string) => Promise<ValueType[]>;
  debounceTimeout?: number;
  placeholder?: string;
}

function DebounceSelect<
  ValueType extends {
    key?: string;
    label: React.ReactNode;
    value: string | number;
    placeholder?: string;
  } = any
>({
  fetchOptions,
  debounceTimeout = 800,
  ...props
}: DebounceSelectProps<ValueType>) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);
  const fetchRef = useRef(0);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return;
        }

        setOptions(newOptions);
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    <Select
      showSearch
      labelInValue
      filterOption={false}
      allowClear
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={options}
    />
  );
}

interface SearchValue {
  label: string;
  value: string;
}
const fetchApi = new CollectMapApi();
const DebouceSearch = (props: {
  onChange: AnyFunc;
  placeholder?: string;
  defaultValue?: string;
  cockpitNumber: string;
  cockpitMode: string;
}) => {
  const [value, setValue] = useState<SearchValue[]>([]);
  const searchVehicleList = (searchText: string): Promise<SearchValue[]> => {
    return new Promise((resolve: Function, reject: Function) => {
      if (props.cockpitMode === CockpitMode.MAP_COLLECTION_MODE) {
        fetchApi.fuzzySearchVehicle(searchText).then((res) => {
          if (res.code === HttpStatusCode.Success) {
            resolve(
              res?.data?.map(
                (item: { vehicleName: string; stationName: string }) => ({
                  label: `${item.vehicleName} ${item.stationName}`,
                  value: item.vehicleName,
                })
              )
            );
          } else {
            reject([]);
          }
        });
      } else {
        request({
          method: Method.POST,
          path: '/monitor/web/cockpit/searchVehicleList',
          body: {
            searchName: searchText || '',
            cockpitNumber: props.cockpitNumber,
          },
        }).then((res) => {
          if (res.code === HttpStatusCode.Success) {
            resolve(
              res?.data?.map(
                (item: { vehicleName: string; stationName: string }) => ({
                  label: `${item.vehicleName} ${item.stationName}`,
                  value: item.vehicleName,
                })
              )
            );
          } else {
            reject([]);
          }
        });
      }
    });
  };
  return (
    <DebounceSelect
      value={value}
      placeholder={props?.placeholder}
      fetchOptions={searchVehicleList}
      onChange={(newValue) => {
        setValue(newValue as SearchValue[]);
        props?.onChange(newValue);
      }}
      style={{ width: '100%' }}
    />
  );
};

export default DebouceSearch;
